import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AdminAttentionService, AttentionCounts } from '@cnt-admin/data-access';
import { IonContent, IonMenuToggle } from '@ionic/angular/standalone';
import { filter } from 'rxjs';

interface INavLink {
  label: string;
  /** Material Symbols ligature name. */
  icon: string;
  link: string[];
  /** Match the URL exactly, so a parent section isn't lit up on its children. */
  exact?: boolean;
  /** Which "Needs attention" count to show as a badge (hidden at 0 or unknown). */
  count?: BadgeCount;
}

type BadgeCount = keyof Pick<AttentionCounts, 'requests' | 'pois' | 'caretakers' | 'payouts'>;

interface INavGroup {
  label: string;
  links: INavLink[];
}

/**
 * Contents of the admin side menu, in cnt-landing's left-rail style (the
 * account page's nav): a floating white card on the cream page with the logo
 * on top, grouped links with Material Symbols icons, and the current section
 * in trinidad. The `ion-menu` itself sits in app.component.html, directly inside
 * the split pane, so it can stay pinned open on wide screens.
 */
@Component({
  selector: 'admin-header',
  standalone: true,
  imports: [IonContent, IonMenuToggle, RouterLink, RouterLinkActive],
  template: `
    <ion-content class="rail">
      <div class="rail-card">
        <a class="brand" routerLink="/" aria-label="CurbNTurf Admin — home">
          <img src="/images/CNT_Logo_V_Orange.svg" alt="" width="34" height="44" />
          <span>
            <strong>CurbNTurf</strong>
            <small>Admin</small>
          </span>
        </a>
        <nav aria-label="Admin sections">
          @for (group of groups; track group.label) {
            <p class="group-label">{{ group.label }}</p>
            @for (item of group.links; track item.label) {
              <ion-menu-toggle [autoHide]="false">
                <a
                  class="nav-item"
                  [routerLink]="item.link"
                  routerLinkActive="active"
                  [routerLinkActiveOptions]="{ exact: !!item.exact }"
                  ariaCurrentWhenActive="page"
                >
                  <span class="material-symbols-outlined" aria-hidden="true">{{ item.icon }}</span>
                  <span class="nav-label">{{ item.label }}</span>
                  @if (item.count && badges[item.count]; as n) {
                    <span class="badge" [attr.aria-label]="n + ' waiting'">{{ n }}</span>
                  }
                </a>
              </ion-menu-toggle>
            }
          }
        </nav>
      </div>
    </ion-content>
  `,
  styles: [
    `
      /* Fill the ion-menu so the rail's ion-content gets a height to scroll in. */
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
      }
      .rail {
        --background: transparent;
        --padding-start: 12px;
        --padding-end: 0;
        --padding-top: 12px;
        --padding-bottom: 12px;
      }
      .rail-card {
        min-height: 100%;
        padding: 20px 12px 24px;
        border: 1px solid var(--cnt-line);
        border-radius: 1rem;
        background: #fff;
        box-shadow: var(--cnt-card-shadow);
      }
      .brand {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 0 8px 20px;
        margin-bottom: 8px;
        border-bottom: 1px solid var(--cnt-line);
        color: var(--cnt-ink);
      }
      .brand span {
        display: flex;
        flex-direction: column;
        line-height: 1.1;
      }
      .brand strong {
        font-size: 1.125rem;
        letter-spacing: -0.01em;
      }
      .brand small {
        margin-top: 2px;
        color: var(--cnt-trinidad);
        font-size: 0.65rem;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
      }
      .group-label {
        margin: 18px 12px 6px;
        color: var(--cnt-muted);
        font-size: 0.65rem;
        font-weight: 700;
        line-height: 1;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }
      .nav-item {
        display: flex;
        align-items: center;
        gap: 12px;
        margin: 2px 0;
        padding: 9px 12px;
        border-radius: 0.75rem;
        color: var(--cnt-ink);
        font-size: 0.875rem;
        font-weight: 700;
        transition:
          background-color 0.15s ease,
          color 0.15s ease;
      }
      .nav-item .material-symbols-outlined {
        flex: none;
        font-size: 20px;
        color: var(--cnt-muted);
      }
      .nav-item:hover {
        background: rgba(247, 245, 236, 0.8);
      }
      .nav-item:focus-visible {
        outline: 2px solid var(--cnt-green);
        outline-offset: 1px;
      }
      .nav-label {
        flex: 1 1 auto;
      }
      .badge {
        flex: none;
        min-width: 22px;
        padding: 2px 7px;
        border-radius: 9999px;
        background: var(--cnt-trinidad);
        color: #fff;
        font-size: 0.7rem;
        font-weight: 700;
        line-height: 1.4;
        text-align: center;
      }
      .nav-item.active {
        background: rgba(var(--cnt-trinidad-rgb), 0.1);
        color: var(--cnt-trinidad);
      }
      .nav-item.active .material-symbols-outlined {
        color: var(--cnt-trinidad);
        font-variation-settings:
          'FILL' 1,
          'wght' 400;
      }
    `,
  ],
})
export class AdminHeaderComponent implements OnInit {
  private readonly attention = inject(AdminAttentionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  /** Badge text per count, e.g. "3" or "99+"; empty when 0 or not loaded. */
  badges: Partial<Record<BadgeCount, string>> = {};

  ngOnInit(): void {
    this.attention.counts$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((c) => {
      const label = (n: number | null) => (n ? (n > 99 ? '99+' : String(n)) : '');
      this.badges = {
        requests: label(c.requests),
        pois: label(c.pois),
        caretakers: label(c.caretakers),
        payouts: label(c.payouts),
      };
    });
    // The menu outlives every page: check the badges after each navigation
    // (a no-op while they're under five minutes old).
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => void this.attention.refreshIfStale());
    void this.attention.refreshIfStale();
  }

  readonly groups: INavGroup[] = [
    {
      label: 'Admin',
      links: [{ label: 'Home', icon: 'home', link: [''], exact: true }],
    },
    {
      label: 'Marketplace',
      links: [
        { label: 'Users', icon: 'person', link: ['', 'users'] },
        { label: 'Reservations', icon: 'confirmation_number', link: ['', 'reservations'], count: 'requests' },
        { label: 'Private', icon: 'cottage', link: ['', 'sites'], exact: true },
        { label: 'Boondocking', icon: 'camping', link: ['', 'sites', 'boondock'] },
        { label: 'Help them finish', icon: 'construction', link: ['', 'unfinished'] },
        { label: 'Hosts to nudge', icon: 'campaign', link: ['', 'nudge'] },
        { label: 'Affiliates', icon: 'group', link: ['', 'affiliates'] },
        { label: 'Payouts', icon: 'account_balance_wallet', link: ['', 'payouts'], count: 'payouts' },
      ],
    },
    {
      label: 'Content',
      links: [
        { label: 'POIs', icon: 'location_on', link: ['', 'pois'], count: 'pois' },
        { label: 'Caretakers', icon: 'key', link: ['', 'rating', 'caretaker'], count: 'caretakers' },
        { label: 'Article Builder', icon: 'edit_note', link: ['', 'article-builder'] },
        { label: 'Email Templates', icon: 'mail', link: ['', 'email-template'] },
      ],
    },
    {
      label: 'Analytics & system',
      links: [
        { label: 'Overview', icon: 'monitoring', link: ['', 'analytics'] },
        { label: 'Search', icon: 'manage_search', link: ['', 'search'] },
        { label: 'Weekly report', icon: 'bar_chart', link: ['', 'reports'] },
        { label: 'Logs', icon: 'dns', link: ['', 'log'] },
      ],
    },
  ];
}
